import type { ConsentModeSignals } from './types';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/**
 * Internal-only. The single place this module speaks to Google.
 *
 * Analogue of `dataLayer.ts` in the analytics foundation: one writer, so the
 * wire format cannot drift per call site. Nothing outside `lib/consent-mode`
 * should call this — go through `consentMode.ts`.
 *
 * WHY THIS IS NOT `pushToDataLayer` FROM `lib/analytics`
 * -----------------------------------------------------
 * They look interchangeable and are not. `pushToDataLayer` pushes a plain
 * OBJECT, which is the correct shape for an analytics event. A gtag command is
 * an ARGUMENTS OBJECT — an array-like whose numeric keys carry
 * `('consent', 'update', {...})`. GTM inspects pushed values and only treats
 * arguments objects as gtag commands; push `['consent', 'update', {...}]` as a
 * real array and it is silently ignored, leaving consent permanently at the
 * default with no error anywhere. Hence the un-idiomatic `arguments` below, and
 * hence a separate writer rather than reaching into the analytics foundation
 * (which Phase 7.0B leaves untouched by design).
 */

/**
 * Push a gtag consent command onto the dataLayer.
 *
 * Declared as a classic function, and pushing `arguments` verbatim, on purpose —
 * see the note above. A rest parameter would produce a real array and break
 * Consent Mode.
 *
 * The parameters are typed even though only `arguments` is read: that is what
 * makes a malformed command a compile error here rather than a signal Google
 * quietly drops. (The canonical JavaScript copy is variadic and untyped; this is
 * the one place the TypeScript rendition is deliberately stricter.)
 *
 * `window.dataLayer` is normally already initialized: the inline snippet in
 * index.html creates it before GTM loads. The `|| []` is defensive, for the case
 * where that snippet never ran (an ad blocker stripped it, or a surface is
 * mis-configured) — commands then queue harmlessly for a GTM that may never
 * arrive, which is inert rather than a crash.
 */
/* eslint-disable @typescript-eslint/no-unused-vars, prefer-rest-params --
 * Both rules are correct in general and wrong here.
 *
 * The three parameters below look unused to ESLint and are not: they are read
 * through `arguments`, which is the only shape GTM accepts (see above). They are
 * declared purely so the compiler checks every call site. Do NOT "fix" this by
 * deleting them or renaming them with a leading underscore — the types are the
 * point.
 *
 * And `arguments` is not a legacy habit here: the rest parameter this rule asks
 * for produces a real array, which GTM silently ignores. Taking the lint advice
 * would leave consent permanently denied with nothing in the console. */
export function gtag(
  command: 'consent',
  subCommand: 'default' | 'update',
  signals: ConsentModeSignals,
): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}
/* eslint-enable @typescript-eslint/no-unused-vars, prefer-rest-params */
